import { useState, useEffect, useCallback } from 'react';
import { getParameterDisplayName } from '../utils/parameterDisplayNames';
import { getPhysicalExamOptionTranslation } from '../utils/physicalExamParameterOptionsVi';

/**
 * Custom hook for managing physical exam form language state and translations
 * Provides functions to translate parameter names and options while maintaining
 * English data storage for backend compatibility
 */
export const usePhysicalExamLanguage = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  // Load language preference from sessionStorage on hook initialization
  useEffect(() => {
    const savedLanguage = sessionStorage.getItem('physicalExamLanguage');
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'vi')) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Handle language change
  const changeLanguage = useCallback((language) => {
    if (language === 'en' || language === 'vi') {
      setCurrentLanguage(language);
      sessionStorage.setItem('physicalExamLanguage', language);
    }
  }, []);

  // Get translated parameter name
  const getTranslatedParameterName = useCallback((parameterKey) => {
    try {
      return getParameterDisplayName(parameterKey, currentLanguage);
    } catch (error) {
      console.warn(`Translation not found for parameter: ${parameterKey}`, error);
      // Fallback to English if Vietnamese translation fails
      try {
        return getParameterDisplayName(parameterKey, 'en');
      } catch (fallbackError) {
        console.error(`No translation found for parameter: ${parameterKey}`, fallbackError);
        return parameterKey; // Return the key itself as last resort
      }
    }
  }, [currentLanguage]);

  // Get translated option value for display
  const getTranslatedOption = useCallback((option) => {
    if (currentLanguage === 'vi') {
      return getPhysicalExamOptionTranslation(option);
    }
    return option;
  }, [currentLanguage]);

  // Translate an array of options for display
  const getTranslatedOptions = useCallback((options) => {
    if (!Array.isArray(options)) return options;
    
    if (currentLanguage === 'vi') {
      return options.map(option => getPhysicalExamOptionTranslation(option));
    }
    return options;
  }, [currentLanguage]);

  // Get placeholder text with translation
  const getTranslatedPlaceholder = useCallback((parameterName) => {
    const translatedName = getTranslatedParameterName(parameterName);
    if (currentLanguage === 'vi') {
      return `Chọn ${translatedName}`;
    }
    return `Select ${translatedName}`;
  }, [currentLanguage, getTranslatedParameterName]);

  // Get translated section title
  const getTranslatedSectionTitle = useCallback((sectionTitle) => {
    if (currentLanguage === 'vi') {
      const sectionTranslations = {
        'Integumentary': 'Hệ thống Da',
        'HEENT': 'Đầu, Mắt, Tai, Mũi, Họng',
        'Neck': 'Cổ',
        'Cardiovascular': 'Tim mạch',
        'Respiratory': 'Hô hấp',
        'Abdominal': 'Bụng',
        'Musculoskeletal (MSK)': 'Cơ xương khớp',
        'Neurological': 'Thần kinh',
        'Lymphatic': 'Hệ bạch huyết',
        'Genitourinary': 'Sinh dục - Tiết niệu',
        'Psychiatric': 'Tâm thần',
        'General': 'Tổng quát'
      };
      return sectionTranslations[sectionTitle] || sectionTitle;
    }
    return sectionTitle;
  }, [currentLanguage]);

  // Get translated form titles and buttons
  const getTranslatedText = useCallback((key) => {
    if (currentLanguage === 'vi') {
      const translations = {
        'Comprehensive Physical Exam': 'Khám Lâm Sàng Toàn Diện',
        'Physical Exam': 'Khám Lâm Sàng',
        'Set All to Normal': 'Đặt Tất Cả về Bình Thường',
        'Save Exam Results': 'Lưu Kết Quả Khám',
        'Cancel': 'Hủy',
        'Close': 'Đóng',
        'Notes': 'Ghi Chú',
        'Optional Notes': 'Ghi Chú Tùy Chọn',
        'Add notes...': 'Thêm ghi chú...',
        'Select': 'Chọn',
        'Enter': 'Nhập',
        'Collapse All': 'Thu Gọn Tất Cả',
        'Expand All': 'Mở Rộng Tất Cả',
        'No exam': 'Không khám'
      };
      return translations[key] || key;
    }
    return key;
  }, [currentLanguage]);

  // Check if current language is Vietnamese
  const isVietnamese = currentLanguage === 'vi';

  // Check if current language is English
  const isEnglish = currentLanguage === 'en';

  return {
    currentLanguage,
    changeLanguage,
    getTranslatedParameterName,
    getTranslatedOption,
    getTranslatedOptions,
    getTranslatedPlaceholder,
    getTranslatedSectionTitle,
    getTranslatedText,
    isVietnamese,
    isEnglish
  };
};

export default usePhysicalExamLanguage;
